import React, { FC, createContext, useEffect, useMemo, useState } from 'react';

import { IPlayerContext, IPlayerProvider } from '../../typings/hlroom';

const PREFIX_ID = 'skyplayer';

const getId = (type: string): string => {
  return `${PREFIX_ID}-${type}`;
};

const loadScript = (): Promise<any> => {
  return new Promise((resolve, reject) => {
    const scriptElement = document.createElement('script');
    scriptElement.src = process.env.REACT_APP_SKYPLAYER_SCRIPT_URL || '';
    scriptElement.id = getId('script');
    document.body.appendChild(scriptElement);
    scriptElement.onload = () => {
      resolve(true);
    };
    scriptElement.onerror = () => reject();
  });
};

const loadStylesheet = (): Promise<any> => {
  return new Promise((resolve, reject) => {
    const linkElement = document.createElement('link');
    linkElement.href = process.env.REACT_APP_SKYPLAYER_STYLESHEET_URL || '';
    linkElement.id = getId('stylesheet');
    linkElement.rel = 'stylesheet';
    linkElement.onload = () => resolve(true);
    linkElement.onerror = () => reject();
    document.head.appendChild(linkElement);
  });
};

export const PlayerContext = createContext<IPlayerContext>({
  loaded: false,
  error: false,
  manager: undefined,
  activePlayer: undefined,
  onSetActivePlayer: () => {},
  onPosterClicked: () => {},
  onTogglePlayerMute: () => {},
  posterClicked: undefined,
  playerMute: false,
});

const PlayerProvider: FC<IPlayerProvider> = ({ children }) => {
  const [activePlayer, setActivePlayer] = useState<string | undefined>(undefined);
  const [posterClicked, setPosterClicked] = useState<string | undefined>(undefined);
  const [playerMute, setPlayerMute] = useState<boolean>(false);

  const [value, setValue] = useState<{ loaded: boolean; error: boolean; manager: undefined | { playerList: any } }>({
    loaded: false,
    error: false,
    manager: undefined,
  });
  useEffect(() => {
    if (value.manager && activePlayer) {
      Object.keys(value.manager?.playerList).forEach((key) => {
        if (!key.includes(activePlayer)) {
          delete value?.manager?.playerList[key];
        }
      });
    }
  }, [value.manager, activePlayer]);

  useEffect(() => {
    (async (_) => {
      try {
        const promisess: Promise<any>[] = [];

        const checkScriptElement = document.querySelector('script[src*="skyplayer/player"][src*=".js"]');
        const checkLinkElement = document.querySelector('link[href*="skyplayer/player"][href*=".css"]');

        !checkScriptElement && promisess.push(loadScript());
        !checkLinkElement && promisess.push(loadStylesheet());

        await Promise.allSettled([...promisess]);

        setValue({
          loaded: true,
          error: false,
          manager: window.SkyPlayersManager && new window.SkyPlayersManager({ noAutoplayPolicy: true }),
        });
      } catch (error) {
        setValue({
          loaded: false,
          error: true,
          manager: undefined,
        });
        throw error;
      }
    })();
  }, []);
  return (
    <PlayerContext.Provider
      value={useMemo(
        () => ({
          loaded: value.loaded,
          error: value.error,
          manager: value.manager,
          activePlayer,
          posterClicked,
          playerMute,
          onSetActivePlayer: (containerId: string) => setActivePlayer(containerId),
          onPosterClicked: (assetId: string) => setPosterClicked(assetId),
          onTogglePlayerMute: (mute: boolean) => setPlayerMute(mute),
        }),
        [activePlayer, value.error, value.loaded, value.manager, posterClicked]
      )}
    >
      {children}
    </PlayerContext.Provider>
  );
};

export default PlayerProvider;
